import {
	city,
	internationalPhoneNumber,
	phoneNumber,
	state,
	streetName,
	streetNumber,
	zipcode,
} from '#base/core/consts/companyData';
import { generalMail } from '#fysio/core/consts/companyData';
import { facebookPageUrl, instagramPageUrl, threadsPageUrl } from '#fysio/core/consts/socialMediaPages';
import type { JobPostingEmploymentType } from '#base/core/enums/JobPostingEmploymentTypeEnum';
import { PhysicalTreatmentEnum } from '#base/core/enums/PhysicalTreatmentEnum';
import { formatSchema, schemaContext } from '#base/helpers/schemaHelpers';
import { Injury, InjuryEnum } from '#fysio/core/enums/InjuryEnum';

export const openingHoursSchema = () => {
	return [
		'Mo,We,Th 08:30-21:00',
		'Tu,Fr 08:30-18:00',
		'We,Th 08:30-21:00',
		'Sa 08:30-13:00',
	];
};

/**
 * GENERAL SCHEMA'S
 */

export const addressSchema = {
	'@type': 'PostalAddress',
	'addressLocality': city,
	'postalCode': zipcode,
	'streetAddress': `${streetName} ${streetNumber}`,
	'addressCountry': 'NL',
	'addressRegion': state,
	'telephone': phoneNumber,
	'email': generalMail,
};

export const organizationSchema = {
	...schemaContext,
	'@type': 'Organization',
	'address': addressSchema,
	'sameAs': [facebookPageUrl, instagramPageUrl, threadsPageUrl],
	'name': 'Fysio Hier',
	'logo': 'https://fysiohier.com/img/logo/fysio-hier-icon-center-dark@2x.png',
	'url': 'https://www.fysiohier.com',
	'contactPoint': {
		'@type': 'ContactPoint',
		'telephone': internationalPhoneNumber,
		'contactType': 'Customer Service',
		'areaServed': 'NL',
		'availableLanguage': [
			{
				'@type': 'Language',
				'name': 'Dutch',
			},
			{
				'@type': 'Language',
				'name': 'English',
			},
		],
	},
};

export const localBusinessSchema = {
	...organizationSchema,
	'@type': 'LocalBusiness',
	'currenciesAccepted': 'EUR',
	'openingHours': openingHoursSchema(),
};

export const medicalBusinessSchema = {
	...localBusinessSchema,
	'@type': 'MedicalBusiness',
	'image': [
		'https://fysiohier.com/img/schema-images/taping_02.jpg',
		'https://fysiohier.com/img/schema-images/hardloop-analyse_01.jpg',
	],
};

/**
 * MEDICAL CONDITION SCHEMA'S
 */

const injurySchema = {
	...schemaContext,
	'@type': 'MedicalCondition',
};

export const injuryKneePainSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.KneePain).title,
		description: 'Pijn in of rondom de knie, door bijvoorbeeld een overbelasting, artrose of andere blessures.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Knie' },
		...additionalContent,
	});

export const injuryKneePainMeniscusInjurySchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.KneePainMeniscusInjury).title,
		description: 'Meniscusletsel kan zorgen voor pijn in de knie, zwelling, een blokkerend of "op slot" schietend gevoel en moeite met buigen of draaien.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Knie' },
		...additionalContent,
	});

export const injuryKneePainRunnersKneeSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.KneePainRunnersKnee).title,
		description: 'Runners knee (ITB-frictiesyndroom) geeft vaak een stekende of brandende pijn aan de buitenzijde van de knie, vooral tijdens hardlopen of traplopen.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Knie' },
		...additionalContent,
	});

export const injuryKneePainJumpersKneeSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.KneePainJumpersKnee).title,
		description: 'Jumpers knee (patellapeesklachten) geeft pijn net onder de knieschijf, vaak bij springen, sprinten, remmen of traplopen. Bij langdurige overbelasting kan sprake zijn van tendinopathie.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Knie' },
		...additionalContent,
	});

export const injuryKneePainOsteoarthritisSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.KneePainOsteoarthritis).title,
		description: 'Knieartrose (slijtage van het kniegewricht) geeft pijn, stijfheid en beperkte beweeglijkheid, vooral bij traplopen, hurken of lang lopen.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Knie' },
		...additionalContent,
	});

export const injuryAnteriorCruciateLigamentRuptureSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.AnteriorCruciateLigamentRupture).title,
		description: 'Een gescheurde voorste kruisband (ACL) kan zorgen voor instabiliteit, wegzakken van de knie, zwelling en pijn na een draaibeweging of sportmoment.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Knie' },
		...additionalContent,
	});

export const injuryTotalKneeProsthesisSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.TotalKneeProsthesis).title,
		description: 'Revalidatie na een totale knieprothese richt zich op het herstellen van mobiliteit, kracht en looppatroon, zodat je weer veilig kunt lopen, fietsen en traplopen.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Knie' },
		...additionalContent,
	});

export const injuryPregnancySchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.PregnancyInjury).title,
		description: 'Zwangerschapsgerelateerde klachten kunnen ontstaan door veranderde belasting en hormonen, met bijvoorbeeld pijn in bekken, rug of lies en moeite met lopen, staan of omdraaien in bed.',
		...additionalContent,
	});

export const injuryNeckPainSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.NeckPain).title,
		description: 'Pijn en stijfheid in de nek, dit kan acuut of langzaam zijn ontstaan.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Nek' },
		...additionalContent,
	});

export const injuryBackPainSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.BackPain).title,
		description: 'Pijn in de lage of bovenrug, die bijvoorbeeld tijdens een beweging acuut erin schieten of langzaam zijn ontstaan.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Rug' },
		...additionalContent,
	});

export const injuryAnklePainSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.AnklePain).title,
		description: 'Pijn in de enkel, dit kan ontstaan door een verzwikking of val of bijvoorbeeld tijdens het sporten.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Enkel' },
		...additionalContent,
	});

export const injuryAnklePainAchillesTendonSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.AnklePainAchillesTendon).title,
		description: 'Achillespeesklachten geven pijn en stijfheid net boven de hiel, vaak bij starten met lopen, traplopen of sporten. De klachten nemen toe bij belasting en na inspanning.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Enkel' },
		...additionalContent,
	});

export const injuryAnklePainSprainedAnkleSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.AnklePainSprainedAnkle).title,
		description: 'Een enkelverzwikking veroorzaakt pijn, zwelling en instabiliteit rond de enkel, vaak na een misstap of sportmoment. Steunen en bewegen kunnen pijnlijk en onzeker aanvoelen.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Enkel' },
		...additionalContent,
	});

export const injuryAnklePainFunctionalInstabilitySchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.AnklePainFunctionalInstability).title,
		description: 'Functionele enkelinstabiliteit uit zich in een onzeker of wegzakkend gevoel in de enkel, vaak na eerdere verzwikkingen, met herhaaldelijk door de enkel gaan bij lopen of sporten.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Enkel' },
		...additionalContent,
	});

export const injuryHielspoorSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.Hielspoor).title,
		description: 'Hielspoor geeft stekende hielpijn bij lopen of opstaan, vooral na rust of bij langdurig staan.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Hiel' },
		...additionalContent,
	});

export const injuryShoulderPainSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.ShoulderPain).title,
		description: 'Klachten in de schouder, door een tendinopathie, slijmbeursontsteking of door een overbelasting wat kan ontstaan tijdens het sporten, thuis of op het werk. ',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Schouder' },
		...additionalContent,
	});

export const injuryHipPainSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.HipPain).title,
		description: 'Pijn in of rond het heupgewricht, vaak door artritis, bursitis of peesontsteking.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Heup' },
		...additionalContent,
	});

export const injuryHipPainOsteoarthritisSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.HipPainOsteoarthritis).title,
		description: 'Heupartrose (slijtage van het heupgewricht) geeft pijn, stijfheid en beperkte beweeglijkheid, vooral bij lopen, traplopen of lang staan.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Heup' },
		...additionalContent,
	});

export const injuryTotalHipProsthesisSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.TotalHipProsthesis).title,
		description: 'Revalidatie na een totale heupprothese helpt je looppatroon, mobiliteit en kracht op te bouwen, zodat je weer comfortabel kunt lopen, fietsen en dagelijkse activiteiten oppakt.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Heup' },
		...additionalContent,
	});

export const injuryHipImpingementSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.HipImpingement).title,
		description: 'Hip impingement (FAI) kan liespijn en stijfheid geven, vooral bij zitten, hurken, draaien of sport. De heup voelt soms geblokkeerd of klikt.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Heup' },
		...additionalContent,
	});

export const injuryLabrumDefectSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.LabrumDefect).title,
		description: 'Een labrumdefect kan diepe lies- of heuppijn geven met klikken, haken of een blokkerend gevoel, vooral bij draaien, buigen, hurken of sporten.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Heup' },
		...additionalContent,
	});

export const injuryTrochanterMajorPainSyndromeSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.TrochanterMajorPainSyndrome).title,
		description: 'Trochanter major pijnsyndroom geeft pijn aan de buitenzijde van de heup, vaak erger bij traplopen, lang staan of liggen op de aangedane zijde.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Heup' },
		...additionalContent,
	});

export const injuryGroinPainSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.GroinPain).title,
		description: 'Pijn in de liesstreek, vaak veroorzaakt door spierverrekkingen of liesbreuken.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Lies' },
		...additionalContent,
	});

export const injuryGroinPainAdductorSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.GroinPainAdductor).title,
		description: 'Adductor gerelateerde liesklachten geven pijn aan de binnenzijde van de lies, vaak bij sprinten, wenden, schieten of knijpen. De klachten kunnen toenemen bij kracht- en veldsport.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Lies' },
		...additionalContent,
	});

export const injuryGroinPainIliopsoasSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.GroinPainIliopsoas).title,
		description: 'Iliopsoas gerelateerde liesklachten geven vaak pijn of stijfheid aan de voorkant van de heup of lies, vooral bij heffen van het been, opstaan uit zitten of (hard)lopen.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Lies' },
		...additionalContent,
	});

export const injuryGroinPainInguinalSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.GroinPainInguinal).title,
		description: 'Inguinale liesklachten kunnen een drukkend of zeurend gevoel in de lies geven, vaak bij hoesten, persen, sprinten of krachttraining. Soms is er sprake van lokale gevoeligheid.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Lies' },
		...additionalContent,
	});

export const injuryGroinPainPubisSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.GroinPainPubis).title,
		description: 'Pubis gerelateerde liesklachten (rond het schaambeen) geven vaak diepe lies- of schaampijn en stijfheid die toeneemt bij sprinten, draaien, schieten of explosieve belasting.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Lies' },
		...additionalContent,
	});

export const injuryGroinPainPelvicFloorSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.GroinPainPelvicFloor).title,
		description: 'Bekkenbodem gerelateerde liesklachten kunnen zich uiten in lies- of onderbuikpijn, een drukkend gevoel rond het bekken en soms klachten bij plassen, ontlasten of vrijen.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Lies' },
		...additionalContent,
	});

export const injuryUpperLegPainSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.UpperLegPain).title,
		description: 'Pijn in het bovenbeen, meestal door spierverrekkingen of zenuwproblemen.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Bovenbeen' },
		...additionalContent,
	});

export const injuryLowerLegPainSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.LowerLegPain).title,
		description: 'Pijn in het onderbeen, vaak door shin splints, spierblessures of circulatieproblemen.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Onderbeen' },
		...additionalContent,
	});

export const injuryElbowPainSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.ElbowPain).title,
		description: 'Pijn in de elleboog, vaak door tenniselleboog, golferselleboog of artritis.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Elleboog' },
		...additionalContent,
	});

export const injuryWristPainSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.WristPain).title,
		description: 'Pijn in de pols, meestal door carpaal tunnelsyndroom, artritis of overbelasting.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Pols' },
		...additionalContent,
	});

export const injuryHeelPainSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.HeelPain).title,
		description: 'Pijn in de hiel, vaak door hielspoor, fasciitis plantaris of overbelasting.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Hiel' },
		...additionalContent,
	});

export const injuryBeaksPainSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.BeaksPain).title,
		description: 'Pijn in het bekkengebied, vaak door bekkeninstabiliteit, artritis of blessures.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Bekken' },
		...additionalContent,
	});

export const injuryHeadAcheSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...injurySchema,
		name: Injury.init(InjuryEnum.HeadAche).title,
		description: 'Pijn in het hoofd, vaak door spanningshoofdpijn, migraine of clusterhoofdpijn.',
		associatedAnatomy: { '@type': 'AnatomicalStructure', 'bodyLocation': 'Hoofd' },
		...additionalContent,
	});

/**
 * MEDICAL THERAPY SCHEMA'S
 */

const physicalTherapySchema = {
	...schemaContext,
	'@type': 'PhysicalTherapy',
};

export const physicalTherapyGeneralSchema = (
	name: string,
	description: string,
	additionalContent?: {
		[key: string]: unknown;
	},
) =>
	formatSchema({
		...physicalTherapySchema,
		name,
		description,
		...additionalContent,
	});

export const physicalTherapyMedicalTapingSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...physicalTherapySchema,
		name: PhysicalTreatmentEnum.MedicalTaping,
		description: 'Om te ondersteunen en pijn te verlichten tijdens je herstel.',
		...additionalContent,
	});

export const physicalTherapyDryNeedlingSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...physicalTherapySchema,
		name: PhysicalTreatmentEnum.DryNeedling,
		description: 'Voor spanning en pijnklachten in spieren.',
		...additionalContent,
	});

export const physicalTherapyShockwaveTherapySchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...physicalTherapySchema,
		name: PhysicalTreatmentEnum.ShockwaveTherapy,
		description: 'Om de eventueel aangedane pezen te behandelen.',
		...additionalContent,
	});

export const physicalTherapyMobilisingSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...physicalTherapySchema,
		name: PhysicalTreatmentEnum.Mobilising,
		description: 'Om de beweeglijkheid weer terug te krijgen.',
		...additionalContent,
	});

export const physicalTherapyGladSchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...physicalTherapySchema,
		name: PhysicalTreatmentEnum.Glad,
		description: 'Voor het behandelen van artrose klachten in heup en knie.',
		...additionalContent,
	});

export const physicalTherapyPregnancySchema = (additionalContent?: {
	[key: string]: unknown;
}) =>
	formatSchema({
		...physicalTherapySchema,
		name: PhysicalTreatmentEnum.PregnancyTherapy,
		description: 'Voor het behandelen van zwangerschapsgerelateerde klachten zoals bekkenpijn, rugpijn en andere fysieke ongemakken tijdens en na de zwangerschap.',
		...additionalContent,
	});

/**
 * JOB POSTINGS
 */

export const jobPostingSchema = (
	title: string,
	description: string,
	datePosted: string,
	employmentTypes: JobPostingEmploymentType[],
) =>
	formatSchema({
		...schemaContext,
		'@type': 'JobPosting',
		title,
		description,
		'jobLocation': {
			'@type': 'Place',
			'address': addressSchema,
		},
		datePosted,
		'hiringOrganization': organizationSchema,
		'employmentType': employmentTypes,
	});
